import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  alpha,
  Container,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import ConstructionIcon from '@mui/icons-material/Construction';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HomeIcon from '@mui/icons-material/Home';
import LayersIcon from '@mui/icons-material/Layers';
import ChairIcon from '@mui/icons-material/Chair';

import bmwDetailImage from 'figma:asset/d30ec8207a5d83317439cd1576ab790533ca9acf.webp';
import mercedesRoomImage from 'figma:asset/f370bc94787c14d2317b94673d0634ec38cf7965.webp';
import privateLoungeImage from '../../imports/private-lounge.webp';
import amgDetailImage from 'figma:asset/09433cfdb6cef19df4a23efcf57e4bae5be903d8.webp';
import classicAZCarsImage from '../../imports/garage3.webp';
import highPileRugImage from 'figma:asset/d960ea2ae017c9c517fff20cc02106b3c07d8387.webp';
import carpetCleaningImage from 'figma:asset/78a4639ad94e671ed42eefd1d3f386ab3118cd06.webp';
import tileStoneImage from 'figma:asset/0469592e0f23d0e2da5ee8b6b5787ba05f83e37d.webp';
import detailStudioGarageImage from '../../imports/detail-studio-garage.webp';

const gold = '#C0202A';
const silver = '#C0C0C0';

const heroSlides = [
  {
    image: detailStudioGarageImage,
    overline: 'Auto & Marine Detailing',
    headline: 'Precision\nPerfected.',
    sub: 'From paint correction to ceramic coating — we restore, protect, and elevate your vehicle to showroom perfection.',
    cta: 'Explore Detailing',
    ctaPath: '/services/auto-detailing',
  },
  {
    image: mercedesRoomImage,
    overline: 'Interior Surface Care',
    headline: 'Your Home\nDeserves Luxury.',
    sub: 'IICRC-certified carpet, tile, and upholstery services that transform your living space to its original brilliance.',
    cta: 'Explore Services',
    ctaPath: '/services/carpet-cleaning',
  },
  {
    image: classicAZCarsImage,
    overline: 'Tucson and Surrounding Areas',
    headline: 'Rooted\nIn Arizona.',
    sub: 'Serving Tucson and surrounding areas since 1994. From classic restorations to modern exotics — every vehicle, every time.',
    cta: 'Our Story',
    ctaPath: '/why-it-matters',
  },
  {
    image: privateLoungeImage,
    overline: 'The Lindsey Standard',
    headline: 'Excellence\nWithout\nCompromise.',
    sub: "Serving Tucson's most discerning clients since 1994. Every project is personal. Every result is extraordinary.",
    cta: 'Our Mission',
    ctaPath: '/why-it-matters',
  },
];

const services = [
  {
    icon: <DirectionsCarIcon sx={{ fontSize: '2rem', color: gold }} />,
    title: 'Auto & Marine Detailing',
    description: 'Paint correction, ceramic coatings, interior restoration, and custom luxury packages for every vehicle.',
    path: '/services/auto-detailing',
    image: amgDetailImage,
  },
  {
    icon: <HomeIcon sx={{ fontSize: '2rem', color: gold }} />,
    title: 'Carpet Cleaning',
    description: 'Deep extraction cleaning designed to preserve fine carpets, rugs, and residential interiors.',
    path: '/services/carpet-cleaning',
    image: highPileRugImage,
  },
  {
    icon: <LayersIcon sx={{ fontSize: '2rem', color: gold }} />,
    title: 'Tile Cleaning & Restoration',
    description: 'Precision cleaning and restoration for both natural stone and all man-made surfaces.',
    path: '/services/tile-grout',
    image: tileStoneImage,
  },
  {
    icon: <ChairIcon sx={{ fontSize: '2rem', color: gold }} />,
    title: 'Upholstery & Leather',
    description: 'Expert care for fine furnishings — fabric, leather, and specialty materials cleaned, conditioned, and protected.',
    path: '/services/upholstery-leather',
    image: 'https://images.unsplash.com/photo-1763979628017-ea650631b0be?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=800',
  },
];

const whyUs = [
  {
    icon: <FamilyRestroomIcon sx={{ fontSize: '2rem' }} />,
    title: 'Every Job Is Personal',
    description: 'Family owned and operated. All work is performed by officers and key principals of the company — never subcontracted. Your satisfaction is our reputation.',
  },
  {
    icon: <ConstructionIcon sx={{ fontSize: '2rem' }} />,
    title: 'Finest Materials & Technique',
    description: 'Only the highest quality products, state-of-the-art equipment, and proven techniques are used. We are qualified to cover all aspects of detailing and surface restoration.',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: '2rem' }} />,
    title: 'IICRC Certified Professionals',
    description: 'Certified by the International Institute of Inspection, Cleaning and Restoration — the gold standard for cleaning professionals. Ongoing education ensures best practices.',
  },
];

const stats = [
  { value: '30+', label: 'Years of Excellence' },
  { value: 'IICRC', label: 'Certified & Trained' },
  { value: '100%', label: 'Satisfaction Guarantee' },
  { value: 'Tucson', label: 'AZ & Surrounding Areas' },
];

export function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  const nextSlide = useCallback(() => {
    setTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      setTransitioning(false);
    }, 500);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 7000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slide = heroSlides[currentSlide];

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          height: '100vh',
          minHeight: 700,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Background Image */}
        <Box
          key={currentSlide}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${slide.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: transitioning ? 0 : 1,
            transition: 'opacity 0.8s ease',
            transformOrigin: currentSlide % 2 === 0 ? 'center center' : 'left center',
            animation: 'kenBurns 9s ease-out forwards',
            '@keyframes kenBurns': {
              '0%': { transform: 'scale(1.08) translate3d(0, 0, 0)' },
              '100%': {
                transform:
                  currentSlide % 2 === 0
                    ? 'scale(1.18) translate3d(-1.5%, -1%, 0)'
                    : 'scale(1.18) translate3d(1.5%, 1%, 0)',
              },
            },
          }}
        />
        {/* Gradient Overlays */}
        {/* B: Stronger directional gradient — left column locked dark, right fades naturally */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(4,4,4,0.76) 0%, rgba(4,4,4,0.65) 40%, rgba(4,4,4,0.41) 65%, rgba(4,4,4,0.05) 100%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: 'linear-gradient(to top, #080808 0%, transparent 100%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '180px',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.41) 55%, transparent 100%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ maxWidth: 700 }}>
            <Box key={`overline-${currentSlide}`}>
              {/* D: Overline + rule switched from crimson to silver */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ width: 40, height: 1, background: silver }} />
                <Typography variant="overline" sx={{ color: silver, fontSize: '0.7rem' }}>
                  {slide.overline}
                </Typography>
              </Box>
            </Box>

            <Box key={`headline-${currentSlide}`}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3.5rem', md: '5.5rem', lg: '7rem' },
                  lineHeight: 0.95,
                  fontWeight: 300,
                  color: '#F2EDE4',
                  mb: 3,
                  whiteSpace: 'pre-line',
                  textShadow: '0 2px 24px rgba(0,0,0,0.9), 0 1px 6px rgba(0,0,0,0.8)',
                }}
              >
                {slide.headline}
              </Typography>
            </Box>

            <Box key={`sub-${currentSlide}`}>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#F2EDE4', 0.85),
                  maxWidth: 520,
                  mb: 5,
                  fontSize: '1rem',
                  lineHeight: 1.9,
                  textShadow: '0 1px 12px rgba(0,0,0,0.95)',
                }}
              >
                {slide.sub}
              </Typography>
            </Box>

            <Box key={`cta-${currentSlide}`}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  component={RouterLink}
                  to={slide.ctaPath}
                  variant="contained"
                  color="primary"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ py: 1.8, px: 5, fontSize: '0.7rem', letterSpacing: '0.2em' }}
                >
                  {slide.cta}
                </Button>
                {/* D: Ghost button — dark fill + white border + white text, never fights the photo */}
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="outlined"
                  size="large"
                  sx={{
                    py: 1.8,
                    px: 5,
                    fontSize: '0.7rem',
                    letterSpacing: '0.2em',
                    borderColor: alpha('#F2EDE4', 0.6),
                    color: '#F2EDE4',
                    backgroundColor: 'rgba(8,8,8,0.55)',
                    '&:hover': {
                      borderColor: '#F2EDE4',
                      backgroundColor: 'rgba(8,8,8,0.75)',
                      color: '#F2EDE4',
                    },
                  }}
                >
                  Request Service
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>

        {/* Slide Indicators */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1.5,
            zIndex: 2,
          }}
        >
          {heroSlides.map((_, i) => (
            <Box
              key={i}
              component="button"
              type="button"
              aria-label={`Show slide ${i + 1}`}
              aria-pressed={i === currentSlide}
              onClick={() => { setCurrentSlide(i); }}
              sx={{
                width: i === currentSlide ? 32 : 8,
                height: 2,
                background: i === currentSlide ? gold : alpha('#F2EDE4', 0.3),
                cursor: 'pointer',
                transition: 'all 0.4s ease',
                border: 0,
                p: 0,
              }}
            />
          ))}
        </Box>

        {/* Scroll Indicator */}
        <Box
          sx={{
            position: 'absolute',
            right: { xs: 20, md: 60 },
            bottom: 40,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            zIndex: 2,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: alpha('#F2EDE4', 0.4),
              fontSize: '0.55rem',
              writingMode: 'vertical-rl',
              letterSpacing: '0.2em',
            }}
          >
            Scroll
          </Typography>
          <Box
            sx={{
              width: 1,
              height: 60,
              background: `linear-gradient(to bottom, ${alpha(gold, 0.5)}, transparent)`,
            }}
          />
        </Box>
      </Box>

      {/* Stats Bar */}
      <Box
        sx={{
          background: '#0D0D0D',
          borderTop: `1px solid ${alpha(gold, 0.12)}`,
          borderBottom: `1px solid ${alpha(gold, 0.12)}`,
          py: 3,
        }}
      >
        <Container maxWidth="lg">
          <Grid container>
            {stats.map((stat, i) => (
              <Grid
                size={{ xs: 6, md: 3 }}
                key={stat.label}
                sx={{
                  borderRight: i < stats.length - 1 ? `1px solid ${alpha(gold, 0.12)}` : 'none',
                  px: { xs: 2, md: 4 },
                  py: { xs: 2, md: 0 },
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: { xs: '1.8rem', md: '2.2rem' },
                    fontWeight: 300,
                    color: gold,
                    lineHeight: 1.1,
                    mb: 0.5,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="overline"
                  sx={{ color: alpha('#F2EDE4', 0.4), fontSize: '0.58rem' }}
                >
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box
        component="section"
        sx={{ py: { xs: 12, md: 18 }, background: '#080808' }}
      >
        <Container maxWidth="md">
          <Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 5 }}>
                <Box sx={{ flex: 1, height: 1, background: `linear-gradient(to right, transparent, ${alpha(gold, 0.4)})` }} />
                <Typography variant="overline" sx={{ color: gold, whiteSpace: 'nowrap' }}>
                  Our Mission
                </Typography>
                <Box sx={{ flex: 1, height: 1, background: `linear-gradient(to left, transparent, ${alpha(gold, 0.4)})` }} />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.2rem', md: '3.5rem' },
                  color: '#F2EDE4',
                  mb: 4,
                  lineHeight: 1.12,
                }}
              >
                <Box component="span" sx={{ display: 'block' }}>
                  To Exceed Every Expectation.
                </Box>
                <Box component="span" sx={{ color: gold, fontStyle: 'italic', display: 'block', mt: 0.75 }}>
                  Every Time.
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: alpha('#F2EDE4', 0.65),
                  maxWidth: 680,
                  mx: 'auto',
                  fontSize: '1rem',
                  lineHeight: 2,
                }}
              >
                Lindsey Enterprises began with one purpose in mind: to deliver a luxury service that very few have ever experienced. From the vehicles you love to the surfaces of your home, we bring the same uncompromising standards to every engagement. The desire to achieve the highest quality results is clearly shown in our work.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Box
        component="section"
        sx={{
          py: { xs: 10, md: 16 },
          background: '#0A0A0A',
          borderTop: `1px solid ${alpha(gold, 0.08)}`,
        }}
      >
        <Container maxWidth="lg">
          <Box>
            <Box sx={{ mb: 8 }}>
              <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
                What We Offer
              </Typography>
              <Typography
                variant="h2"
                sx={{ fontSize: { xs: '2rem', md: '3rem' }, color: '#F2EDE4' }}
              >
                Our Services
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={0.5}>
            {services.map((service, i) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={service.title}>
                <Box sx={{ height: '100%' }}>
                  <Box
                    component={RouterLink}
                    to={service.path}
                    aria-label={`Learn more about ${service.title}`}
                    sx={{
                      position: 'relative',
                      height: { xs: 300, md: 420 },
                      width: '100%',
                      display: 'block',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: 0,
                      p: 0,
                      textAlign: 'left',
                      textDecoration: 'none',
                      '&:hover .service-overlay': {
                        opacity: 1,
                      },
                      '&:hover .service-bg': {
                        transform: 'scale(1.05)',
                      },
                      '&:hover .service-arrow': {
                        transform: 'translateX(6px)',
                      },
                    }}
                  >
                    <Box
                      className="service-bg"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${service.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        transition: 'transform 0.6s ease',
                        filter: 'brightness(0.45)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to top, rgba(4,4,4,0.9) 0%, transparent 60%)',
                      }}
                    />
                    <Box
                      className="service-overlay"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: alpha(gold, 0.08),
                        opacity: 0,
                        transition: 'opacity 0.4s ease',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 4,
                      }}
                    >
                      <Box sx={{ mb: 2 }}>{service.icon}</Box>
                      <Typography
                        variant="h5"
                        sx={{
                          color: '#F2EDE4',
                          mb: 1.5,
                          fontSize: { xs: '1.2rem', md: '1.35rem' },
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: alpha('#F2EDE4', 0.6),
                          fontSize: '0.83rem',
                          lineHeight: 1.7,
                          mb: 2,
                        }}
                      >
                        {service.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="overline"
                          sx={{ color: gold, fontSize: '0.62rem' }}
                        >
                          Learn More
                        </Typography>
                        <ArrowForwardIcon
                          className="service-arrow"
                          sx={{
                            color: gold,
                            fontSize: '0.9rem',
                            transition: 'transform 0.3s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box
        component="section"
        sx={{
          py: { xs: 12, md: 18 },
          background: '#080808',
          borderTop: `1px solid ${alpha(gold, 0.08)}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Box>
                <Typography variant="overline" sx={{ color: gold, display: 'block', mb: 2 }}>
                  Why Choose Us
                </Typography>
                <Typography
                  variant="h2"
                  sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, color: '#F2EDE4', mb: 4, lineHeight: 1.15 }}
                >
                  The Difference Is{' '}
                  <Box component="span" sx={{ color: gold, fontStyle: 'italic' }}>in the Details.</Box>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: alpha('#F2EDE4', 0.6), mb: 5, lineHeight: 2 }}
                >
                  Whether it is your daily driver or your dream car, your home or your business — we are more than qualified to exceed your expectations. All work is performed by officers and key individuals of the company, never subcontracted.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/why-it-matters"
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ py: 1.8, px: 5, fontSize: '0.7rem', letterSpacing: '0.2em' }}
                >
                  Why It Matters
                </Button>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {whyUs.map((item, i) => (
                  <Box key={item.title}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 4,
                        py: 5,
                        borderBottom: `1px solid ${alpha(gold, 0.1)}`,
                        '&:first-of-type': { borderTop: `1px solid ${alpha(gold, 0.1)}` },
                        transition: 'all 0.3s',
                        '&:hover': {
                          background: alpha(gold, 0.03),
                          pl: 2,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          border: `1px solid ${alpha(gold, 0.3)}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: gold,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ color: '#F2EDE4', mb: 1.5, fontSize: '1.25rem' }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: alpha('#F2EDE4', 0.6), lineHeight: 1.85, fontSize: '0.88rem' }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Testimonial Quote */}
      <Box
        sx={{
          py: { xs: 12, md: 18 },
          borderTop: `1px solid ${alpha(gold, 0.08)}`,
          borderBottom: `1px solid ${alpha(gold, 0.08)}`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Luxury lobby background */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${carpetCleaningImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.18) saturate(0.6)',
          }}
        />
        {/* Dark overlay for legibility */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(8,8,8,0.6) 0%, rgba(8,8,8,0.75) 100%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: { xs: '16rem', md: '24rem' },
            color: alpha(gold, 0.04),
            lineHeight: 1,
            pointerEvents: 'none',
            userSelect: 'none',
            fontWeight: 700,
          }}
        >
          "
        </Box>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 4 }}>
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} sx={{ color: gold, fontSize: '1rem' }} />
                ))}
              </Box>
              <Typography
                variant="h3"
                sx={{
                  color: '#F2EDE4',
                  fontStyle: 'italic',
                  fontWeight: 300,
                  lineHeight: 1.5,
                  mb: 4,
                  fontSize: { xs: '1.4rem', md: '2rem' },
                }}
              >
                "Lindsey's team treated my vehicle like it was their own. The paint correction work was absolutely flawless — the results exceeded every expectation I had."
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <Box sx={{ width: 32, height: 1, background: gold }} />
                <Typography variant="overline" sx={{ color: gold, fontSize: '0.62rem' }}>
                  Michael T. · Tucson, AZ
                </Typography>
                <Box sx={{ width: 32, height: 1, background: gold }} />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Banner */}
      <Box
        sx={{
          py: { xs: 12, md: 18 },
          background: '#080808',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Box>
            <Chip
              label="IICRC Certified · Family Owned · Since 1994"
              sx={{
                background: alpha(gold, 0.1),
                color: gold,
                border: `1px solid ${alpha(gold, 0.3)}`,
                borderRadius: 0,
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.6rem',
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                mb: 4,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3.5rem' },
                color: '#F2EDE4',
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              Ready to Experience the{' '}
              <Box component="span" sx={{ color: gold, fontStyle: 'italic' }}>
                Lindsey Standard?
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha('#F2EDE4', 0.6),
                mb: 6,
                maxWidth: 560,
                mx: 'auto',
                lineHeight: 2,
              }}
            >
              Contact us today to schedule your complimentary consultation. We serve Tucson, Marana, Oro Valley, Vail, Sahuarita, Green Valley, and Sierra Vista.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/contact#booking-form"
                variant="contained"
                color="primary"
                size="large"
                sx={{ py: 2, px: 6, fontSize: '0.7rem', letterSpacing: '0.2em' }}
              >
                Schedule a Service
              </Button>
              <Button
                component={RouterLink}
                to="/gallery"
                variant="outlined"
                color="primary"
                size="large"
                sx={{ py: 2, px: 6, fontSize: '0.7rem', letterSpacing: '0.2em' }}
              >
                View Gallery
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
